<template>
  <div class="tradie-card">
    <div
      class="tradie-row"
      role="button"
      tabindex="0"
      @click="$emit('toggle', rowKey)"
      @keydown.enter.self="$emit('toggle', rowKey)"
      @keydown.space.self.prevent="$emit('toggle', rowKey)"
    >
      <div class="tradie-row-left">
        <b-icon-chevron-right
          class="expand-icon"
          :class="{ expanded: isExpanded }"
        />
        <span
          class="tradie-avatar"
          :class="{ 'tradie-avatar-inactive': inactive }"
          :style="avatarStyle"
          >{{ initials }}</span
        >
        <div class="tradie-identity">
          <span
            class="tradie-name"
            :class="{ 'tradie-name-inactive': inactive }"
            >{{ row.tradieName }}</span
          >
          <span v-if="row.trade" class="tradie-trade">{{ row.trade }}</span>
        </div>
        <span
          v-if="leaveSummary"
          class="leave-badge"
          :title="leaveSummary.label"
        >
          {{ leaveSummary.label
          }}<template v-if="leaveSummary.count > 1">
            · {{ leaveSummary.count }}d</template
          >
        </span>
      </div>
      <div class="tradie-row-status">
        <span
          class="status-pill status-pill-sm"
          :class="inactive ? 'status-inactive' : 'status-active'"
        >
          {{ inactive ? 'Inactive' : 'Active' }}
        </span>
      </div>
      <div
        class="tradie-row-hours"
        :class="{ 'tradie-row-hours-empty': !(row.totalHours > 0) }"
      >
        {{ row.totalHours > 0 ? row.totalHours + 'h' : '-' }}
      </div>
      <div class="tradie-row-summary">
        <span
          v-if="row.pendingCount > 0"
          class="status-pill status-pill-sm status-pending"
        >
          {{ row.pendingCount }} pending
        </span>
        <div v-if="showNoEntries && row.totalEntries === 0" class="no-entries">
          No entries this week.
        </div>
        <b-button
          v-if="approvableEvents.length > 0 && canEditTimesheet"
          size="sm"
          variant="success"
          class="action-btn"
          :disabled="approvingAll"
          @click.stop="$emit('approve-all', row)"
        >
          <b-spinner v-if="approvingAll" small class="mr-1" />
          Approve All
        </b-button>
      </div>
    </div>

    <div v-if="isExpanded" class="tradie-detail">
      <timesheet-tradie-week-block
        :row="row"
        :row-key="rowKey"
        :project-id="projectId"
        :week-days="weekDays"
        :selected-day="selectedDay"
        :can-create-timesheet="canCreateTimesheet"
        :can-edit-timesheet="canEditTimesheet"
        :can-delete-timesheet="canDeleteTimesheet"
        :is-supervisor-tradie="isSupervisorTradie"
        :editing-event-id="editingEventId"
        :edit-form="editForm"
        :max-future-date="maxFutureDate"
        @day-click="$emit('day-click', $event)"
        @view-event="$emit('view-event', $event)"
        @approve="forwardApprove"
        @reject="$emit('reject', $event)"
        @clock-out="$emit('clock-out', $event)"
        @edit="$emit('edit', $event)"
        @save-edit="$emit('save-edit', $event)"
        @cancel-edit="$emit('cancel-edit')"
        @delete="$emit('delete', $event)"
        @edit-leave="$emit('edit-leave', $event)"
        @delete-leave="$emit('delete-leave', $event)"
        @lightbox="forwardLightbox"
      />
    </div>
  </div>
</template>

<script>
import { getApprovableEvents } from '@/utils/timesheetHelpers'
import {
  rowLeaveSummary,
  workerAvatarStyle,
  workerInitials,
} from '@/utils/timesheetRosterPresentation'
import TimesheetTradieWeekBlock from '@/components/timesheets/list/TimesheetTradieWeekBlock.vue'

export default {
  name: 'TradieRow',
  components: { TimesheetTradieWeekBlock },
  props: {
    row: { type: Object, required: true },
    rowKey: { type: String, required: true },
    projectId: { type: [Number, String], default: null },
    weekDays: { type: Array, default: () => [] },
    selectedDay: { type: String, default: null },
    isExpanded: { type: Boolean, default: false },
    canCreateTimesheet: { type: Boolean, default: false },
    canEditTimesheet: { type: Boolean, default: false },
    canDeleteTimesheet: { type: Boolean, default: false },
    isSupervisorTradie: { type: Boolean, default: false },
    // Only the single-project (flat list) view shows the "No entries this
    // week." hint next to a row with zero entries.
    showNoEntries: { type: Boolean, default: false },
    // Styles the name gray (matches InactiveTradieSection's old inline
    // style) for a rostered-but-no-events-this-week tradie. Every other
    // part of the row already degrades correctly on its own: totalHours,
    // pendingCount, and approvableEvents are all naturally 0 for a tradie
    // with zero events, so the pills/Approve All button just don't render.
    inactive: { type: Boolean, default: false },
    editingEventId: { type: [Number, String], default: null },
    editForm: { type: Object, default: null },
    maxFutureDate: { type: String, default: '' },
    approvingAll: { type: Boolean, default: false },
  },
  computed: {
    approvableEvents() {
      return getApprovableEvents(this.row)
    },
    initials() {
      return workerInitials(this.row.tradieName)
    },
    avatarStyle() {
      return workerAvatarStyle(this.row.tradieName)
    },
    leaveSummary() {
      return rowLeaveSummary(this.row)
    },
  },
  methods: {
    forwardApprove(eventId, decision) {
      this.$emit('approve', eventId, decision)
    },
    forwardLightbox(urls, index) {
      this.$emit('lightbox', urls, index)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/layouts/timesheet-shared';

/* Moved from TimesheetListTab.vue's scoped style, which used to render this
   markup inline — Vue scoped CSS only reaches a child component's root
   element, never its internal nodes, so none of these rules ever applied
   once this row was split into its own component. */
.tradie-card {
  background: #fff;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
}

.tradie-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 90px 90px minmax(90px, auto);
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  user-select: none;
  gap: 12px;
  transition: background-color 0.15s;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr) 90px minmax(90px, auto);
  }

  &:hover {
    background-color: #f8fafc;
  }

  &:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: -2px;
  }
}

.tradie-row-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.expand-icon {
  font-size: 12px;
  color: #9ca3af;
  flex-shrink: 0;
  transition: transform 0.2s;

  &.expanded {
    transform: rotate(90deg);
  }
}

.tradie-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;

  &.tradie-avatar-inactive {
    opacity: 0.55;
  }
}

.tradie-identity {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.3;
}

.tradie-name {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.tradie-name-inactive {
    color: #9ca3af;
  }
}

.tradie-trade {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.leave-badge {
  font-size: 11px;
  font-weight: 600;
  color: #c2410c;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  padding: 2px 8px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;
}

.tradie-row-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #374151;

  @media (max-width: 900px) {
    display: none;
  }
}

.tradie-row-hours {
  text-align: right;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  font-variant-numeric: tabular-nums;

  &.tradie-row-hours-empty {
    font-weight: 400;
    color: #d1d5db;
  }
}

.tradie-row-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.no-entries {
  font-size: 13px;
  color: #9ca3af;
  padding: 8px 0;
}

.action-btn {
  white-space: nowrap;
  font-size: 12px;
  border-radius: 8px;
  height: auto;
  padding: 0.25rem 0.625rem;
}

.tradie-detail {
  border-top: 1px solid #f3f4f6;
  padding: 12px 16px 16px;
  background: #fcfcfd;
}

/* Phones: name + pills + Approve All can't share one line — the name
   gets crushed to a letter and the buttons collide. Stack the card instead:
   chevron + name on top, the pills/actions on their own row beneath, aligned
   with the name. */
@media (max-width: 575.98px) {
  .tradie-row {
    grid-template-columns: minmax(0, 1fr) auto;
    row-gap: 8px;
    padding: 12px 14px;
  }

  .tradie-row-left {
    grid-column: 1 / -1;
  }

  .tradie-row-hours {
    margin-left: 54px;
    text-align: left;
  }

  .tradie-row-summary {
    justify-content: flex-end;
    padding-left: 0;
  }
}
</style>
